import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import ChangeLog from './ChangeLog';
import useScrollLock from '../../hooks/useScrollLock';
import { processImage, isImageFile, validateFileSize, MAX_IMAGE_MB, formatSizeLimit } from '../../utils/imageProcessor';
import './Salon.css';

const AddEditAccessoryModal = ({ isOpen, onClose, salonId, accessory, onAccessoryAdded, onAccessoryUpdated }) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    type: 'rest',
    stock_count: '1'
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errors, setErrors] = useState({});
  const modalBodyRef = useRef(null);
  const originalAccessoryRef = useRef(null);
  const [isChangeLogVisible, setIsChangeLogVisible] = useState(false);
  const shouldScrollToFirstErrorRef = useRef(false);

  // Lock body scroll when modal is open
  useScrollLock(isOpen);

  // Scroll to first error field when form is submitted with errors
  useEffect(() => {
    if (shouldScrollToFirstErrorRef.current && Object.keys(errors).length > 0 && modalBodyRef.current) {
      // Order of fields in the form (to find first error)
      const fieldOrder = ['name', 'type'];
      
      // Find the first field with an error
      const firstErrorField = fieldOrder.find(field => errors[field]);
      
      if (firstErrorField) {
        // Small delay to ensure DOM is updated
        setTimeout(() => {
          const errorElement = document.getElementById(firstErrorField);
          if (errorElement) {
            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Focus the field to make it even more obvious
            errorElement.focus();
          }
          // Reset the flag after scrolling
          shouldScrollToFirstErrorRef.current = false;
        }, 100);
      } else {
        // If no specific field error found, reset flag
        shouldScrollToFirstErrorRef.current = false;
      }
    } else if (error && modalBodyRef.current && !shouldScrollToFirstErrorRef.current) {
      // If there's a general error (not field-specific), scroll to top
      modalBodyRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [error, errors]);

  useEffect(() => {
    if (accessory) {
      const accessoryData = {
        name: accessory.name || '',
        type: accessory.type || 'rest',
        stock_count: accessory.stock_count ?? '',
        image: accessory.image || ''
      };
      setFormData({
        name: accessoryData.name,
        type: accessoryData.type,
        stock_count: accessoryData.stock_count
      });
      originalAccessoryRef.current = accessoryData;
      setPreviewUrl(accessory.image || '');
      setIsChangeLogVisible(false);
    } else {
      setFormData({
        name: '',
        type: 'rest',
        stock_count: '1'
      });
      originalAccessoryRef.current = null;
      setPreviewUrl('');
      setIsChangeLogVisible(false);
    }
    setSelectedFile(null);
    setError('');
    setErrors({});
    // Reset scroll flag when modal opens
    shouldScrollToFirstErrorRef.current = false;
  }, [accessory, isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Reset scroll flag when user starts typing (they're fixing errors)
    shouldScrollToFirstErrorRef.current = false;
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file type
      if (!isImageFile(file)) {
        setError(t('messages.error.invalidImage', 'Only image files are allowed'));
        return;
      }

      // Check file size (the original - it is resized before upload)
      if (!validateFileSize(file)) {
        setError(t('messages.error.fileSizeMustBeLessThan', '', { size: formatSizeLimit(MAX_IMAGE_MB) }));
        return;
      }

      try {
        // Process image (resize and compress)
        const processedFile = await processImage(file);
        
        setSelectedFile(processedFile);
        setError('');

        // Create preview URL
        const reader = new FileReader();
        reader.onload = (e) => {
          setPreviewUrl(e.target.result);
        };
        reader.readAsDataURL(processedFile);
      } catch (error) {
        console.error('Error processing image:', error);
        setError(t('messages.error.failedToProcessImage'));
      }
    }
  };

  const getLocalizedFieldName = (fieldName) => {
    const fieldNameMap = {
      'name': 'accessoryName',
      'type': 'accessoryType',
      'stock_count': 'stockCount',
      'image': 'image'
    };

    const translationKey = fieldNameMap[fieldName];
    if (translationKey) {
      return t(`accessories.${translationKey}`);
    }

    return fieldName
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const logAccessoryChanges = async (originalAccessory, updatedData) => {
    if (!originalAccessory || !accessory?.id) {
      return;
    }

    const trackedFields = ['name', 'type', 'stock_count', 'image'];

    const sanitizeValue = (value) => {
      if (value === null || value === undefined || value === '') return '';
      if (typeof value === 'number') return String(value);
      return String(value).trim();
    };

    const changes = trackedFields.reduce((acc, field) => {
      const previousValue = sanitizeValue(originalAccessory[field]);
      const newValue = sanitizeValue(updatedData[field]);
      if (previousValue !== newValue) {
        acc.push({
          field_name: field,
          previous_value: previousValue || null,
          new_value: newValue || null
        });
      }
      return acc;
    }, []);

    if (changes.length === 0) return;

    const token = localStorage.getItem('token');
    if (!token) {
      console.error('logAccessoryChanges: No token found');
      return;
    }

    try {
      await Promise.all(changes.map(change =>
        axios.post(
          `/api/salons/${salonId}/change-logs`,
          {
            entity_type: 'accessory',
            entity_id: accessory.id,
            field_name: change.field_name,
            previous_value: change.previous_value,
            new_value: change.new_value,
            change_type: 'update',
            metadata: { accessory_id: accessory.id }
          },
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        ).catch(error => {
          console.error('Error logging accessory change:', {
            field: change.field_name,
            error: error.response?.data || error.message,
            status: error.response?.status
          });
        })
      ));
    } catch (error) {
      console.error('logAccessoryChanges: Error logging changes', error);
    }
  };

  const handleToggleChangeLog = () => {
    if (!accessory?.id) return;
    setIsChangeLogVisible(!isChangeLogVisible);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name || !formData.name.trim()) {
      newErrors.name = t('messages.error.accessoryNameRequired');
    }
    
    if (!formData.type) {
      newErrors.type = t('messages.error.accessoryTypeRequired');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (loading) {
      return;
    }
    
    // Validate required fields
    if (!validateForm()) {
      // Set flag to scroll to first error on next render
      shouldScrollToFirstErrorRef.current = true;
      return;
    }
    
    setLoading(true);
    setError('');
    setErrors({});

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('type', formData.type);
      formDataToSend.append('stock_count', formData.stock_count);

      if (selectedFile) {
        formDataToSend.append('image', selectedFile);
      }

      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      };

      if (accessory) {
        const response = await axios.put(`/api/salons/${salonId}/accessories/${accessory.id}`, formDataToSend, config);
        if (response.status === 200) {
          await logAccessoryChanges(
            originalAccessoryRef.current || {
              name: accessory.name || '',
              type: accessory.type || 'rest',
              stock_count: accessory.stock_count ?? '',
              image: accessory.image || ''
            },
            {
              name: formData.name,
              type: formData.type,
              stock_count: formData.stock_count,
              image: selectedFile ? 'updated' : previewUrl
            }
          );
        }
      } else {
        await axios.post(`/api/salons/${salonId}/accessories`, formDataToSend, config);
      }
      
      // Call callbacks after setting loading to false
      setLoading(false);
      
      if (accessory) {
        onAccessoryUpdated();
      } else {
        onAccessoryAdded();
      }
    } catch (err) {
      console.error('Error saving accessory:', err);
      setError(err.response?.data?.error || t('messages.error.failedToSaveAccessory'));
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h3>{accessory ? t('accessories.editAccessory') : t('accessories.addAccessory')}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-body" ref={modalBodyRef}>
          <form onSubmit={handleSubmit} id="base-form" noValidate>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">{t('accessories.accessoryName')} *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={errors.name ? 'error' : ''}
                  placeholder={t('accessories.accessoryName')}
                />
                {errors.name && <span className="error-message">{errors.name}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="type">{t('accessories.accessoryType')} *</label>
                <select
                  id="type"
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  className={errors.type ? 'error' : ''}
                >
                  <option value="handbag">{t('accessories.handbag')}</option>
                  <option value="coat">{t('accessories.coat')}</option>
                  <option value="wrap">{t('accessories.wrap')}</option>
                  <option value="bolero">{t('accessories.bolero')}</option>
                  <option value="tiara">{t('accessories.tiara')}</option>
                  <option value="sash">{t('accessories.sash')}</option>
                  <option value="fur_jacket">{t('accessories.furJacket')}</option>
                  <option value="veil">{t('accessories.veil')}</option>
                  <option value="rest">Rest</option>
                </select>
                {errors.type && <span className="error-message">{errors.type}</span>}
              </div>

          <div className="form-group">
            <label htmlFor="stock_count">{t('accessories.stockCount')}</label>
            <input
              type="number"
              id="stock_count"
              name="stock_count"
              value={formData.stock_count}
              onChange={handleInputChange}
              min="0"
              placeholder={t('accessories.stockCount')}
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="image">{t('accessories.uploadImage')}</label>
          <div className="image-upload-container">
            <input
              type="file"
              id="image"
              name="image"
              accept="image/*"
              onChange={handleFileChange}
              className="file-input"
            />
            <label htmlFor="image" className="file-input-label">
              <span className="upload-icon">📷</span>
              {t('accessories.imageOptional')}
            </label>
            {previewUrl && (
              <div className="image-preview">
                <img src={previewUrl} alt="Preview" className="preview-image" />
                <button type="button" onClick={() => { setSelectedFile(null); setPreviewUrl(''); }} className="remove-image-btn">
                  ✕
                </button>
              </div>
            )}
          </div>
        </div>

        {accessory && (
          <>
            <div className="form-section">
              <div style={{ textAlign: 'center', display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="sleek-button change-log"
                  onClick={handleToggleChangeLog}
                  disabled={loading}
                >
                  {isChangeLogVisible ? t('reservations.hideChangeLog') : t('reservations.viewChangeLog')}
                </button>
              </div>
            </div>

            <ChangeLog
              salonId={salonId}
              entityType="accessory"
              entityId={accessory.id}
              getLocalizedFieldName={getLocalizedFieldName}
              isVisible={isChangeLogVisible}
              onToggle={handleToggleChangeLog}
              scrollContainerRef={modalBodyRef}
              titleKey="reservations.changeLogTitle"
              namespace="reservations"
            />
          </>
        )}

            {error && <div className="error-message">{error}</div>}
          </form>
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="salon-button secondary"
            onClick={onClose}
            disabled={loading}
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            form="base-form"
            className="salon-button primary"
            disabled={loading}
          >
            {loading ? (accessory ? t('reservations.updating') : t('reservations.creating')) : (accessory ? t('common.saveChanges') : t('accessories.addAccessory'))}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddEditAccessoryModal;
